import { useCallback } from "react";
import { useListaDeduplicacaoApi } from "../../../infra/geo-veles";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../presentation/hooks/store-hooks";
import { useModalContainerService } from "../../modal-container";
import { useUIService } from "../../ui";
import { getDistinctValues, onlyNumbers, validateCPF } from "../../../utils";
import {
  resetCreateListaDeduplicacaoFormState,
  resetEditListaDeduplicacaoFormState,
  resetListaDeduplicacaoFileDataState,
  resetListasDeduplicacaoState,
  setCPFsEditListaDeduplicacaoState,
  setCreateListaDeduplicacaoFormErrorsState,
  setCreateListaDeduplicacaoFormTouchedState,
  setCreateListaDeduplicacaoNomeState,
  setEditListaDeduplicacaoAddCPFState,
  setEditListaDeduplicacaoAtivaState,
  setEditListaDeduplicacaoEditCPFState,
  setEditListaDeduplicacaoFormErrorsState,
  setEditListaDeduplicacaoFormTouchedState,
  setEditListaDeduplicacaoIdVelesGeoListaDeduplicacaoState,
  setEditListaDeduplicacaoNomeState,
  setEditListaDeduplicacaoRemoveCPFState,
  setListaDeduplicacaoDistinctValuesState,
  setListaDeduplicacaoFileDataListState,
  setListaDeduplicacaoFileDataState,
  setListaDeduplicacaoFileInvalidDataListState,
  setListaDeduplicacaoFileRepeatedDataListState,
  setListaDeduplicacaoFileValidDataListState,
  setListaDeduplicacaoSelectedColumnState,
  setListaDeduplicacaoValidValuesState,
  setListasDeduplicacaoState,
  setOpenCreateListaDeduplicacaoState,
  setOpenEditCPFState,
  setOpenEditListaDeduplicacaoState,
  setOpenImportFileDeduplicacaoState,
  setOpenListasDeduplicacaoState,
  setOpenPreviewListaDeduplicacaoState,
  setSelectedListasDeduplicacaoState,
  validateCreateListaDeduplicacaoForm,
  validateEditListaDeduplicacaoForm,
} from "../../../application/states/geo-veles";
import { Box, Button, Grid, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { IListaDeduplicacao } from "../../../application/models";

export const useListaDeduplicacaoService = () => {
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const {
    user: { currentUser },
    listaDeduplicacao,
  } = useAppSelector((state) => state);
  // Hook
  const { openModal, closeModal } = useModalContainerService();
  const { setLoading, setAlert } = useUIService();
  // API
  const {
    createListaDeduplicacaoApi,
    findListasDeduplicacaoApi,
    updateListaDeduplicacaoApi,
    updateListaDeduplicacaoStatusApi,
  } = useListaDeduplicacaoApi();

  const findCPFKeyInArray = useCallback((array: any[]) => {
    for (let i = 0; i < array.length; i++) {
      const keys = Object.keys(array[i]);
      for (let k = 0; k < keys.length; k++) {
        if (validateCPF(onlyNumbers(`${array[i][keys[k]]}`))) {
          return keys[k];
        }
      }
    }
    return null;
  }, []);

  const resetListaDeduplicacaoFileData = useCallback(() => {
    dispatch(resetListaDeduplicacaoFileDataState());
  }, [dispatch]);

  const selectColumn = useCallback(
    (data: { selectedColumn: string; data?: any[] }) => {
      const loadingId = Math.random().toString();

      setLoading({
        id: loadingId,
        loading: true,
        message: "Selecionando coluna...",
      });

      dispatch(
        setListaDeduplicacaoSelectedColumnState({
          selectedColumn: data.selectedColumn,
        })
      );
      /* performatic code to get distinct values */
      let dataToUse = listaDeduplicacao.fileData.data;
      if (data.data) {
        dataToUse = data.data;
      }

      const distinctValues = getDistinctValues(
        dataToUse.map((item: any) => item[data.selectedColumn])
      );

      dispatch(
        setListaDeduplicacaoDistinctValuesState({
          distinctValues: distinctValues.length,
        })
      );

      const filteredList = [...distinctValues].filter((item) => {
        const replacedItem = onlyNumbers(String(item));
        return !listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs.includes(
          replacedItem
        );
      });

      const distinctValidValues = [...distinctValues].filter((value: any) => {
        let isValid = validateCPF(onlyNumbers(value));

        if (isValid) {
          return true;
        } else {
          return false;
        }
      });

      const repeatedList = [...distinctValues].filter((item) => {
        const replacedItem = onlyNumbers(item);
        return listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs.includes(
          replacedItem
        );
      });

      const validValues = [...filteredList].filter((value: any) => {
        let isValid = validateCPF(onlyNumbers(value));
        if (isValid) {
          return true;
        } else {
          return false;
        }
      });

      const invalidValues = [...filteredList].filter((value: any) => {
        let isValid = validateCPF(onlyNumbers(value));
        if (!isValid) {
          return true;
        } else {
          return false;
        }
      });

      dispatch(
        setListaDeduplicacaoValidValuesState({
          validValues: distinctValidValues.length,
        })
      );
      dispatch(
        setListaDeduplicacaoFileDataListState({
          value: validValues.map((cep) => onlyNumbers(cep)),
        })
      );
      dispatch(
        setListaDeduplicacaoFileValidDataListState({
          value: validValues.map((cep) => onlyNumbers(cep)),
        })
      );
      dispatch(
        setListaDeduplicacaoFileInvalidDataListState({
          value: invalidValues.map((cep) => onlyNumbers(cep)),
        })
      );
      dispatch(
        setListaDeduplicacaoFileRepeatedDataListState({
          value: repeatedList.map((cep) => onlyNumbers(cep)),
        })
      );

      setLoading({
        id: loadingId,
        loading: false,
      });
    },
    [
      dispatch,
      listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs,
      listaDeduplicacao.fileData.data,
      setLoading,
    ]
  );

  const setFile = useCallback(
    (data: {
      name: string;
      size: string;
      columns: string[];
      type: string;
      data: any[];
    }) => {
      const loadingId = Math.random().toString();

      setLoading({
        id: loadingId,
        loading: true,
        message: "Lendo arquivo...",
      });

      dispatch(
        setListaDeduplicacaoFileDataState({
          fileData: {
            columns: data.columns,
            data: data.data,
            name: data.name,
            size: data.size,
            type: data.type,
          },
        })
      );

      const cepKey = findCPFKeyInArray(data.data);

      if (cepKey) {
        const replacedFileData = [];
        for (let i = 0; i < data.data.length; i++) {
          const cep = data.data[i];

          if (typeof cep[cepKey] === "number") {
            const replacedCnpj = onlyNumbers(String(cep[cepKey]) as string);

            replacedFileData.push(replacedCnpj);
          }
          if (typeof cep[cepKey] === "string") {
            const replacedCnpj = onlyNumbers(String(cep[cepKey]));

            replacedFileData.push(replacedCnpj);
          }
        }

        const filteredList = replacedFileData.filter((item) => {
          return !listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs.includes(
            item
          );
        });

        const repeatedList = replacedFileData.filter((item) => {
          const replacedItem = onlyNumbers(item);
          return listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs.includes(
            replacedItem
          );
        });

        const validValues = filteredList.filter((value: string) => {
          const numericCep = onlyNumbers(value);

          let isValid = validateCPF(numericCep);
          if (isValid) {
            return numericCep;
          } else {
            return false;
          }
        });

        if (validValues.length < 1) {
          if (repeatedList.length > 0) {
            setLoading({
              id: loadingId,
              loading: false,
            });

            openModal({
              content: (
                <>
                  <Box p={3}>
                    <Grid
                      container
                      alignItems={"center"}
                      flexDirection="column"
                    >
                      <Grid item>
                        <ErrorIcon color="secondary" sx={{ fontSize: 100 }} />
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="h4"
                          sx={{ mt: 2 }}
                          align="center"
                          textTransform="uppercase"
                        >
                          Encontramos apenas CPFs já inseridos na sua lista
                          atual.
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="body1"
                          sx={{ mt: 2 }}
                          align="center"
                        >
                          Não encontramos nenhuma coluna com CPFs válidos e
                          novos no arquivo. Selecione um arquivo com CPFs novos
                          válidos.
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box my={3}>
                          <Button
                            variant="contained"
                            onClick={() => {
                              closeModal();
                              resetListaDeduplicacaoFileData();
                            }}
                          >
                            Ok
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              ),
            });
          } else {
            setLoading({
              id: loadingId,
              loading: false,
            });

            openModal({
              content: (
                <>
                  <Box p={3}>
                    <Grid
                      container
                      alignItems={"center"}
                      flexDirection="column"
                    >
                      <Grid item>
                        <ErrorIcon color="secondary" sx={{ fontSize: 100 }} />
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="h4"
                          sx={{ mt: 2 }}
                          align="center"
                          textTransform="uppercase"
                        >
                          Não encontramos CPFs válidos no seu arquivo
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="body1"
                          sx={{ mt: 2 }}
                          align="center"
                        >
                          Não encontramos nenhuma coluna com CPFs válidos e
                          novos. Selecione outro arquivo.
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box my={3}>
                          <Button
                            variant="contained"
                            onClick={() => {
                              closeModal();
                              resetListaDeduplicacaoFileData();
                            }}
                          >
                            Ok
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              ),
            });
          }
        } else {
          setLoading({
            id: loadingId,
            loading: false,
          });

          selectColumn({
            selectedColumn: cepKey,
            data: data.data,
          });

          openModal({
            content: (
              <>
                <Box p={3}>
                  <Grid container alignItems={"center"} flexDirection="column">
                    <Grid item>
                      <CheckCircleIcon color="primary" sx={{ fontSize: 100 }} />
                    </Grid>
                    <Grid item>
                      <Typography
                        variant="h4"
                        sx={{ mt: 2 }}
                        align="center"
                        textTransform="uppercase"
                      >
                        Arquivo <strong>{data.name}</strong> carregado com
                        sucesso!
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1" sx={{ mt: 2 }} align="center">
                        Selecionamos a coluna <strong>{cepKey}</strong> como a
                        coluna que contém os CPFs. Caso esta não seja a coluna
                        correta, selecione outra no menu indicado.
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Box my={3}>
                        <Button
                          variant="contained"
                          onClick={() => {
                            closeModal();
                          }}
                        >
                          Ok
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </>
            ),
          });
        }
      } else {
        setLoading({
          id: loadingId,
          loading: false,
        });

        openModal({
          content: (
            <>
              <Box p={3}>
                <Grid container alignItems={"center"} flexDirection="column">
                  <Grid item>
                    <ErrorIcon color="secondary" sx={{ fontSize: 100 }} />
                  </Grid>
                  <Grid item>
                    <Typography
                      variant="h4"
                      sx={{ mt: 2 }}
                      align="center"
                      textTransform="uppercase"
                    >
                      Não encontramos CPFs válidos no seu arquivo
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1" sx={{ mt: 2 }} align="center">
                      Não encontramos nenhuma coluna com CPFs válidos. Selecione
                      outro arquivo.
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Box my={3}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          closeModal();
                          resetListaDeduplicacaoFileData();
                        }}
                      >
                        Ok
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          ),
        });
      }
    },
    [
      setLoading,
      dispatch,
      findCPFKeyInArray,
      listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs,
      openModal,
      closeModal,
      resetListaDeduplicacaoFileData,
      selectColumn,
    ]
  );

  const setCreateListaDeduplicacaoName = useCallback(
    (Nome: string) => {
      dispatch(setCreateListaDeduplicacaoNomeState({ Nome }));
    },
    [dispatch]
  );

  const setEditListaDeduplicacaoName = useCallback(
    (nome: string) => {
      dispatch(setEditListaDeduplicacaoNomeState({ nome }));
    },
    [dispatch]
  );

  const resetForm = useCallback(() => {
    dispatch(resetEditListaDeduplicacaoFormState());
  }, [dispatch]);

  const addFileListToListaDeduplicacao = useCallback(() => {
    const loadingId = Math.random().toString();

    try {
      setLoading({
        id: loadingId,
        loading: true,
        message: "Lendo arquivo...",
      });

      /* Do not insert if there is no list selected */
      if (!listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs) {
        return;
      }
      /* Do not insert repeated values */
      const filteredList = [
        ...listaDeduplicacao.fileListData.fileListData,
      ].filter((item) => {
        return !listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs.includes(
          item
        );
      });

      if (filteredList.length > 0) {
        dispatch(
          setCPFsEditListaDeduplicacaoState({
            CPFs: [
              ...listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs,
              ...listaDeduplicacao.fileListData.fileListTableValues
                .fileListValidData,
            ],
          })
        );

        setAlert({
          message: "Lista adicionada com sucesso!",
          type: "success",
        });
      }
    } catch (error) {
      setAlert({
        message: "Erro ao ler arquivo",
        type: "error",
      });
    } finally {
      setLoading({
        id: loadingId,
        loading: false,
      });
    }
  }, [
    setLoading,
    listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs,
    listaDeduplicacao.fileListData.fileListData,
    listaDeduplicacao.fileListData.fileListTableValues.fileListValidData,
    dispatch,
    setAlert,
  ]);

  const setEditListaDeduplicacaoFields = useCallback(
    (data: { listaDeduplicacao: IListaDeduplicacao }) => {
      dispatch(
        setSelectedListasDeduplicacaoState({
          listaDeduplicacao: data.listaDeduplicacao,
        })
      );

      dispatch(
        setEditListaDeduplicacaoIdVelesGeoListaDeduplicacaoState({
          IdVelesGeoListaDeduplicacao:
            data.listaDeduplicacao.IdVelesGeoListaDeduplicacao,
        })
      );

      dispatch(
        setEditListaDeduplicacaoNomeState({
          nome: data.listaDeduplicacao.Nome,
        })
      );

      dispatch(
        setEditListaDeduplicacaoAtivaState({
          Status: data.listaDeduplicacao.Ativa,
        })
      );

      dispatch(
        setCPFsEditListaDeduplicacaoState({
          CPFs: data.listaDeduplicacao.CPFs ?? [],
        })
      );
    },
    [dispatch]
  );

  const selectListaDeduplicacaoToEdit = useCallback(
    (IdVelesGeoListaDeduplicacao: number) => {
      const listaDeduplicacaoToEdit = listaDeduplicacao.listasDeduplicacao.find(
        (item) =>
          item.IdVelesGeoListaDeduplicacao === IdVelesGeoListaDeduplicacao
      );

      if (listaDeduplicacaoToEdit) {
        setEditListaDeduplicacaoFields({
          listaDeduplicacao: listaDeduplicacaoToEdit,
        });

        dispatch(setOpenEditListaDeduplicacaoState(true));
      }
    },
    [
      dispatch,
      listaDeduplicacao.listasDeduplicacao,
      setEditListaDeduplicacaoFields,
    ]
  );

  const addItemToDeduplicacaoList = useCallback(
    (item: string) => {
      const replacedCPF = onlyNumbers(item);

      if (!validateCPF(replacedCPF)) {
        setAlert({
          message: "CPF inválido",
          type: "error",
        });
      } else {
        if (
          listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs.includes(
            replacedCPF
          )
        ) {
          setAlert({
            message: "Este CPF já se encontra na lista",
            type: "error",
          });
        }

        if (
          listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs.includes(
            replacedCPF
          ) === false
        ) {
          dispatch(
            setEditListaDeduplicacaoAddCPFState({
              CPF: replacedCPF,
            })
          );

          setAlert({
            message: "CPF adicionado com sucesso",
            type: "success",
          });
        }
      }
    },
    [
      dispatch,
      listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs,
      setAlert,
    ]
  );

  const listaDeduplicacaoEditCPF = useCallback(
    (data: { CPF: string; newCPF: string }) => {
      const novoCPFPadronizado = onlyNumbers(data.newCPF);

      if (validateCPF(novoCPFPadronizado)) {
        if (
          listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs.includes(
            novoCPFPadronizado
          )
        ) {
          setAlert({
            message: "Este CPF já se encontra na lista atual",
            type: "error",
          });
        } else {
          dispatch(
            setEditListaDeduplicacaoEditCPFState({
              oldCPF: data.CPF,
              newCPF: novoCPFPadronizado,
            })
          );

          setAlert({
            message: "CPF atualizado com sucesso",
            type: "success",
          });
        }
      } else {
        setAlert({
          message: "CPF inválido",
          type: "error",
        });
      }
    },
    [
      dispatch,
      listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs,
      setAlert,
    ]
  );

  const listaDeduplicacaoRemoveCPF = useCallback(
    (CPF: string) => {
      dispatch(setEditListaDeduplicacaoRemoveCPFState({ CPF }));

      setAlert({
        message: "CPF removido com sucesso",
        type: "success",
      });
    },
    [dispatch, setAlert]
  );

  const setupListaDeduplicacao = useCallback(async () => {
    if (currentUser) {
      try {
        const getListasDeduplicacao = await findListasDeduplicacaoApi({
          IdUsuario: currentUser.IdUsuario,
        });

        dispatch(
          setListasDeduplicacaoState({
            listasDeduplicacao: getListasDeduplicacao,
          })
        );
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: "error",
        });
      }
    }
  }, [currentUser, findListasDeduplicacaoApi, dispatch, setAlert]);

  const getListasDeduplicacaoExtracao = useCallback(
    (data: { listasDeduplicacaoExtracao: IListaDeduplicacao[] }) => {
      dispatch(
        setListasDeduplicacaoState({
          listasDeduplicacao: data.listasDeduplicacaoExtracao,
        })
      );
    },
    [dispatch]
  );

  const createListaDeduplicacao = useCallback(async () => {
    if (currentUser) {
      const loadingId = Math.random().toString();
      try {
        const errors = validateCreateListaDeduplicacaoForm(
          listaDeduplicacao.createListaDeduplicacaoForm.fields
        );

        if (errors.length > 0) {
          dispatch(
            setCreateListaDeduplicacaoFormErrorsState({
              errors: errors,
            })
          );

          dispatch(
            setCreateListaDeduplicacaoFormTouchedState({
              touched: true,
            })
          );
        } else {
          setLoading({
            id: loadingId,
            loading: true,
            message: "Criando lista...",
          });

          const createListaDeduplicacao = await createListaDeduplicacaoApi({
            IdUsuario: currentUser.IdUsuario,
            Nome: listaDeduplicacao.createListaDeduplicacaoForm.fields.Nome,
          });

          dispatch(
            setListasDeduplicacaoState({
              listasDeduplicacao: [
                ...listaDeduplicacao.listasDeduplicacao,
                createListaDeduplicacao,
              ],
            })
          );

          dispatch(setOpenCreateListaDeduplicacaoState(false));
          selectListaDeduplicacaoToEdit(
            createListaDeduplicacao.IdVelesGeoListaDeduplicacao
          );
          setAlert({
            message: "Lista criada com sucesso",
            type: "success",
          });
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: "error",
        });
      } finally {
        setLoading({
          id: loadingId,
          loading: false,
        });
      }
    }
  }, [
    currentUser,
    listaDeduplicacao.createListaDeduplicacaoForm.fields,
    listaDeduplicacao.listasDeduplicacao,
    dispatch,
    setLoading,
    createListaDeduplicacaoApi,
    selectListaDeduplicacaoToEdit,
    setAlert,
  ]);

  const updateListaDeduplicacao = useCallback(async () => {
    const loadingId = Math.random().toString();
    try {
      const errors = validateEditListaDeduplicacaoForm(
        listaDeduplicacao.editListaDeduplicacaoForm.fields
      );

      if (errors.length > 0) {
        dispatch(
          setEditListaDeduplicacaoFormErrorsState({
            errors: errors,
          })
        );

        dispatch(
          setEditListaDeduplicacaoFormTouchedState({
            touched: true,
          })
        );
      } else {
        setLoading({
          id: loadingId,
          loading: true,
          message: "Atualizando lista...",
        });

        await updateListaDeduplicacaoApi({
          IdVelesGeoListaDeduplicacao:
            listaDeduplicacao.editListaDeduplicacaoForm.fields
              .IdVelesGeoListaDeduplicacao,
          CPFs: listaDeduplicacao.editListaDeduplicacaoForm.fields.CPFs,
          Nome: listaDeduplicacao.editListaDeduplicacaoForm.fields.Nome,
        });

        setAlert({
          message: "Lista atualizada com sucesso",
          type: "success",
        });
        closeEditListaDeduplicacaoForm();
        setupListaDeduplicacao();
      }
    } catch (error: any) {
      setAlert({
        message: error.response.data.error,
        type: "error",
      });
    } finally {
      setLoading({
        id: loadingId,
        loading: false,
      });
    }
  }, [
    listaDeduplicacao.editListaDeduplicacaoForm.fields,
    dispatch,
    setLoading,
    updateListaDeduplicacaoApi,
    setAlert,
  ]);

  const updateListaDeduplicacaoStatus = useCallback(
    async (data: { IdVelesGeoListaDeduplicacao: number; Status: boolean }) => {
      try {
        await updateListaDeduplicacaoStatusApi({
          IdVelesGeoListaDeduplicacao: data.IdVelesGeoListaDeduplicacao,
          Status: data.Status,
        });

        if (data.Status === false) {
          setAlert({
            message: "Lista excluída com sucesso",
            type: "success",
          });
        }

        await setupListaDeduplicacao();
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: "error",
        });
      }
    },
    [setAlert, setupListaDeduplicacao, updateListaDeduplicacaoStatusApi]
  );

  const verifyListaDeduplicacaoFileData = useCallback(() => {
    const loadingId = Math.random().toString();

    setLoading({
      id: loadingId,
      loading: true,
      message: "Validando CPFs do arquivo...",
    });

    const validCPFS =
      listaDeduplicacao.fileListData.fileListTableValues.fileListValidData;
    const invalidCPFS =
      listaDeduplicacao.fileListData.fileListTableValues.fileListInvalidData;

    if (validCPFS.length < 1 && invalidCPFS.length > 0) {
      setLoading({
        id: loadingId,
        loading: false,
      });

      openModal({
        content: (
          <>
            <Box p={3}>
              <Grid container alignItems={"center"} flexDirection="column">
                <Grid item>
                  <ErrorIcon color="secondary" sx={{ fontSize: 100 }} />
                </Grid>
                <Grid item>
                  <Typography
                    variant="h4"
                    sx={{ mt: 2 }}
                    align="center"
                    textTransform="uppercase"
                  >
                    A coluna{" "}
                    <strong>
                      {" "}
                      {listaDeduplicacao.matchCode.selectedColumn}{" "}
                    </strong>{" "}
                    não possui CPFs válidos.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" sx={{ mt: 2 }} align="center">
                    Por favor, selecione uma coluna com CPFs válidos, ou outro
                    arquivo.
                  </Typography>
                </Grid>
                <Grid item>
                  <Box my={3}>
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      onClick={() => {
                        closeModal();
                      }}
                    >
                      Ok
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        ),
      });

      return false;
    } else {
      setLoading({
        id: loadingId,
        loading: false,
      });

      return true;
    }
  }, [
    setLoading,
    listaDeduplicacao.fileListData.fileListTableValues.fileListValidData,
    listaDeduplicacao.fileListData.fileListTableValues.fileListInvalidData,
    listaDeduplicacao.matchCode.selectedColumn,
    openModal,
    closeModal,
  ]);

  const openCreateListaDeduplicacaoForm = useCallback(
    (openForm: boolean) => {
      if (!openForm) {
        dispatch(resetCreateListaDeduplicacaoFormState());
      }
      dispatch(setOpenCreateListaDeduplicacaoState(openForm));
    },
    [dispatch]
  );

  const closeEditListaDeduplicacaoForm = useCallback(() => {
    dispatch(setOpenEditListaDeduplicacaoState(false));
    dispatch(resetEditListaDeduplicacaoFormState());
  }, [dispatch]);

  const openImportFileListaDeduplicacao = useCallback(
    (openForm: boolean) => {
      dispatch(setOpenImportFileDeduplicacaoState(openForm));
    },
    [dispatch]
  );

  const openPreviewListaDeduplicacao = useCallback(
    (openForm: boolean) => {
      dispatch(setOpenPreviewListaDeduplicacaoState(openForm));
    },
    [dispatch]
  );

  const openEditCPF = useCallback(
    (openForm: boolean) => {
      dispatch(setOpenEditCPFState(openForm));
    },
    [dispatch]
  );

  const resetListasDeduplicacao = useCallback(() => {
    dispatch(resetListasDeduplicacaoState());
  }, [dispatch]);

  const setOpenListasDeduplicacao = useCallback(
    (open: boolean) => {
      dispatch(setOpenListasDeduplicacaoState(open));
    },
    [dispatch]
  );

  return {
    findCPFKeyInArray,
    resetListaDeduplicacaoFileData,
    selectColumn,
    setFile,
    setCreateListaDeduplicacaoName,
    setEditListaDeduplicacaoName,
    resetForm,
    addFileListToListaDeduplicacao,
    setEditListaDeduplicacaoFields,
    selectListaDeduplicacaoToEdit,
    addItemToDeduplicacaoList,
    listaDeduplicacaoEditCPF,
    listaDeduplicacaoRemoveCPF,
    setupListaDeduplicacao,
    createListaDeduplicacao,
    updateListaDeduplicacao,
    updateListaDeduplicacaoStatus,
    verifyListaDeduplicacaoFileData,
    openCreateListaDeduplicacaoForm,
    closeEditListaDeduplicacaoForm,
    openImportFileListaDeduplicacao,
    openPreviewListaDeduplicacao,
    openEditCPF,
    resetListasDeduplicacao,
    setOpenListasDeduplicacao,
    getListasDeduplicacaoExtracao
  };
};
